// /settings/account — danger / safety surface per W2 §3.3.2 #18.
// MVP scope: change-password + logout. (Email-resend / delete-account deferred.)
//
// W39.79. This page used to call `api.POST` directly and hand-roll its error:
//
//     throw new ApiError(res.response.status, "AUTH_CHANGE_PWD_FAILED", "更改密碼失敗")
//
// which replaced the server's `code` **and** its `details` with one hardcoded
// pair. Measured on a 393×852 render, every failure the form can produce landed
// on the same one-line banner:
//
//   | input                    | server                                     | on screen        |
//   |--------------------------|--------------------------------------------|------------------|
//   | empty form               | 422 VALIDATION_FAILED + 2 field details    | 更改密碼失敗       |
//   | new password of 3 chars  | 422 VALIDATION_FAILED + "at least 8"       | 更改密碼失敗       |
//   | wrong current password   | 401 AUTH_INVALID_CREDENTIALS                | 更改密碼失敗       |
//
// The third row is the one that made the handler a lie rather than a gap: the
// `catch` below had a branch written specifically to say 「舊密碼不正確」, and it
// could not fire, because the code it tested for had just been overwritten. Three
// branches, none reachable.
//
// So: `changePassword` from `lib/api/auth` — which already runs the shared
// `apiError()` mapper — and the `details` array routed to the field it names.
// `lib/api/pair.ts::ApiError` has carried `details?: {field, reason, message}[]`
// since W2; this page was the one caller that dropped it.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { PageContainer, SectionStack } from "@/components/layout";
import { ApiError, changePassword, logout } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/AuthProvider";
import { PASSWORD_MIN_LENGTH, passwordHint, passwordTooShortMessage } from "@/lib/passwordPolicy";

interface FieldErrors {
  old_password?: string;
  new_password?: string;
  form?: string;
}

/**
 * Route a `VALIDATION_FAILED` payload onto the fields it names.
 *
 * The server's `field` is `body.new_password` and its `message` is English
 * ("String should have at least 8 characters"), neither of which belongs in a
 * zh-Hant form. So the **field** is taken from the payload — that is the part
 * only the server knows — and the **sentence** is built here, from
 * `passwordPolicy`, which a test pins to the contract.
 *
 * Unknown fields are counted rather than dropped: a payload naming something this
 * form has no input for must still produce a form-level line, or the person is
 * told nothing at all.
 */
function errorsFromDetails(details: ApiError["details"]): FieldErrors {
  const out: FieldErrors = {};
  let unplaced = 0;
  for (const d of details ?? []) {
    const name = d.field.replace(/^body\./, "");
    if (name === "old_password" || name === "new_password") {
      out[name] =
        d.reason === "TOO_SHORT" && name === "new_password"
          ? passwordTooShortMessage()
          : d.reason === "TOO_SHORT"
            ? "請輸入現有密碼"
            : "請填寫這一欄";
    } else {
      unplaced += 1;
    }
  }
  if (unplaced > 0) out.form = "更改密碼失敗, 請檢查上面的欄位。";
  return out;
}

export function AccountPage() {
  const nav = useNavigate();
  const { user, refresh, markSignedOut } = useAuth();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const submitPwd = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setOkMsg(null);
    try {
      // The form sets `noValidate`, so the browser does not enforce
      // `minLength={PASSWORD_MIN_LENGTH}` on this element — a guard that only
      // asserted the attribute would pass forever. The bound is checked here
      // instead, and the same sentence is used either way, so what a person is
      // told does not depend on which layer caught it.
      if (newPwd.length < PASSWORD_MIN_LENGTH) {
        setErrors({ new_password: passwordTooShortMessage() });
        return;
      }
      await changePassword({ old_password: oldPwd, new_password: newPwd });
      setOkMsg("密碼已更新, 其他裝置的登入已被撤銷。");
      setOldPwd("");
      setNewPwd("");
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "AUTH_INVALID_CREDENTIALS") {
          setErrors({ old_password: "舊密碼不正確" });
        } else if (err.code === "VALIDATION_FAILED") {
          setErrors(errorsFromDetails(err.details));
        } else if (err.code === "AUTH_PASSWORD_TOO_SHORT") {
          // Defence in depth. Unreachable through HTTP today:
          // `ChangePasswordRequest.new_password` has `min_length=8`, so FastAPI
          // answers 422 `VALIDATION_FAILED` before the route body runs and
          // `crud.change_password`'s own length check never executes. Kept
          // because the service is callable from a script, and removed on the
          // day that is not true.
          setErrors({ new_password: passwordTooShortMessage() });
        } else {
          setErrors({ form: err.message || "更改密碼失敗" });
        }
      } else {
        setErrors({ form: "更改密碼時發生錯誤" });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      // Tell the auth provider this was on purpose. Without it, "signed out" and
      // "signed out because you logged in elsewhere" are the same state, and the
      // login page would tell someone they were evicted when they pressed the
      // button themselves.
      markSignedOut();
      await refresh();
      nav("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ form: err.message });
      } else {
        setErrors({ form: "登出時發生錯誤" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-page" className="flex flex-col">
      <PageHero
          // W52.0 D-9 — no trail, so the only exit was the browser's back.
          crumbs={[
            { to: "/settings", label: "設定", testId: "account-back" },
            { label: "帳戶安全" },
          ]}
          eyebrow="帳戶安全"
          title="密碼與登入"
          subtitle="更新密碼、登出, 或管理你的帳戶。"
        />

      <PageContainer vertical="default">
        <SectionStack density="default">
          <Card>
            <div className="p-s-6 flex flex-col gap-s-4" data-testid="account-identity-card">
              <header>
                <h2 className="text-title-3 text-ink-1">登入身份</h2>
                <p className="text-body-sm text-ink-3 mt-s-1">
                  {user?.display_name}
                  {user?.email ? ` · ${user.email}` : ``}
                </p>
              </header>
            </div>
          </Card>

          <Card>
            <div className="p-s-6 flex flex-col gap-s-4" data-testid="account-password-card">
              <header>
                <h2 className="text-title-3 text-ink-1">更改密碼</h2>
                <p className="text-body-sm text-ink-3 mt-s-1">
                  更新後, 其他裝置將被自動登出 (per ADR-0008 rotate-on-action)。
                </p>
              </header>
              {/* `noValidate` is deliberate and stays: the browser's own bubble
                  follows the OS locale, which would put an English sentence on a
                  zh-Hant form and outside the app's own copy. The bound it would
                  have enforced is checked in `submitPwd` instead, in the same
                  words the hint uses. W39.79 measured what this pair used to do
                  together: the attribute was declared, `noValidate` made it
                  inert, and a three-character password produced one line reading
                  「更改密碼失敗」. */}
              <form onSubmit={submitPwd} className="flex flex-col gap-s-4" noValidate>
                <FormErrorBanner message={errors.form} />
                {okMsg ? (
                  <p
                    data-testid="pwd-success"
                    role="status"
                    className="text-body-sm text-you-strong bg-paper-calm border border-you rounded-md px-s-3 py-s-2"
                  >
                    {okMsg}
                  </p>
                ) : null}
                <FormField
                  name="old_password"
                  label="現有密碼"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={oldPwd}
                  onChange={setOldPwd}
                  invalid={Boolean(errors.old_password)}
                  errorMessage={errors.old_password}
                  disabled={busy}
                />
                <FormField
                  name="new_password"
                  label="新密碼"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                  value={newPwd}
                  onChange={setNewPwd}
                  invalid={Boolean(errors.new_password)}
                  errorMessage={errors.new_password}
                  disabled={busy}
                  hint={passwordHint()}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy}
                  data-testid="submit-change-password"
                  leadingIcon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                >
                  {busy ? "更新中…" : "更新密碼"}
                </Button>
              </form>
            </div>
          </Card>

          <Card>
            <div className="p-s-6 flex flex-col gap-s-4" data-testid="account-logout-card">
              <header>
                <h2 className="text-title-3 text-ink-1">登出</h2>
                <p className="text-body-sm text-ink-3 mt-s-1">
                  登出後, 你需要重新輸入帳戶資料才能繼續使用 heshe。
                </p>
              </header>
              <Button
                variant="destructive"
                onClick={handleLogout}
                disabled={busy}
                data-testid="submit-logout"
              >
                登出這個裝置
              </Button>
            </div>
          </Card>
        </SectionStack>
      </PageContainer>
    </div>
  );
}
